import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// El wizard de 5 pasos (título/descripción/encargado/duración/supervisor) vive sobre un único
// `<input>` que cambia de significado por paso -- ver `handleSubtaskKeyDown` en el container,
// que sigue siendo el dueño del avance de pasos. Esta hoja solo pinta el paso actual.
export function SubtaskCreateWizard({
    canCreate, subtaskInputState, subtaskFilteredUsers, subtaskSelectedUserIndex, subtaskForm,
    newSubtaskTitle, systemUsers,
    setSubtaskInputState, setNewSubtaskTitle, setSubtaskFilteredUsers, setSubtaskSelectedUserIndex,
    handleCreateSubtask, handleSubtaskKeyDown,
}) {
    const { t: translate } = useTranslation('tasks');

    return (
        <div style={{ position: 'relative', marginTop: 'var(--space-1)' }}>
            {canCreate && (
                <>
                    {(subtaskInputState === 2 || subtaskInputState === 4) && subtaskFilteredUsers.length > 0 && (
                        <ul style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, backgroundColor: '#222', borderRadius: 'var(--radius-md)', border: '1px solid #333', padding: 'var(--space-1)', margin: '0 0 var(--space-1) 0', listStyle: 'none', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                            {subtaskFilteredUsers.slice(0, 3).map((u, i) => (
                                <li
                                    key={u.id}
                                    style={{ padding: 'var(--space-2) var(--space-2)', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', backgroundColor: i === subtaskSelectedUserIndex ? '#333' : 'transparent', borderRadius: 'var(--radius-sm)' }}
                                    onClick={() => {
                                        if (subtaskInputState === 2) {
                                            subtaskForm.setValue('assignee', u.nick);
                                            setSubtaskInputState(3);
                                        } else {
                                            handleCreateSubtask(u.nick);
                                        }
                                        setNewSubtaskTitle('');
                                    }}
                                    onMouseEnter={() => setSubtaskSelectedUserIndex(i)}
                                >
                                    <img src={(u['link-profile-img-thumb'] || u['link-profile-img']) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${u.nick}`} alt={u.nick} loading="lazy" style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }} />
                                    <span style={{ color: '#fff', fontSize: '13px' }}>{u.nick}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {subtaskInputState === 3 && (
                        <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, display: 'flex', gap: '6px', padding: 'var(--space-2)', margin: '0 0 var(--space-1) 0', backgroundColor: '#222', borderRadius: 'var(--radius-md)', border: '1px solid #333', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                            {[
                                { label: '5 min', val: 5 },
                                { label: '20min', val: 20 },
                                { label: '40 min', val: 40 },
                                { label: '3horas', val: 180 },
                                { label: '7horas', val: 420 }
                            ].map(tag => (
                                <div
                                    key={tag.val}
                                    onClick={() => {
                                        subtaskForm.setValue('duration', tag.val);
                                        setSubtaskInputState(4);
                                        setNewSubtaskTitle('');
                                        setSubtaskFilteredUsers(systemUsers);
                                        setSubtaskSelectedUserIndex(0);
                                    }}
                                    style={{ padding: 'var(--space-1) var(--space-3)', backgroundColor: 'rgba(255,255,255,0.08)', color: '#fff', borderRadius: 'var(--radius-lg)', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', border: '1px solid rgba(255,255,255,0.1)' }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'}
                                >
                                    {tag.label}
                                </div>
                            ))}
                        </div>
                    )}
                    {subtaskInputState > 0 && (
                        <div
                            onClick={() => setSubtaskInputState(prev => Math.max(0, prev - 1))}
                            style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 5 }}
                            title={translate('modal.subtaskBackAria')}
                            aria-label={translate('modal.subtaskBackAria')}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                        >
                            <ChevronLeft size={14} color="#ccc" />
                        </div>
                    )}
                    <input
                        type="text"
                        className="task-panel-description-textarea subtask-input"
                        style={{ width: '100%', height: 'var(--h-md)', minHeight: 'var(--h-md)', padding: `8px 12px 8px ${subtaskInputState > 0 ? '36px' : '12px'}`, fontSize: '13px', lineHeight: 'normal' }}
                        placeholder={subtaskInputState === 0 ? translate('modal.subtaskPlaceholders.title') : (subtaskInputState === 1 ? translate('modal.subtaskPlaceholders.description') : (subtaskInputState === 2 ? translate('modal.subtaskPlaceholders.assignee') : (subtaskInputState === 3 ? translate('modal.subtaskPlaceholders.duration') : translate('modal.subtaskPlaceholders.supervisor'))))}
                        aria-label={translate('modal.subtaskAssistantAria')}
                        value={newSubtaskTitle}
                        aria-invalid={subtaskInputState === 0 && !!subtaskForm.formState.errors.title}
                        onChange={(e) => {
                            const val = e.target.value;
                            setNewSubtaskTitle(val);
                            if (subtaskInputState === 0 && subtaskForm.formState.errors.title) {
                                subtaskForm.clearErrors('title');
                            }
                            if (subtaskInputState === 2 || subtaskInputState === 4) {
                                const matches = systemUsers.filter(u => u.nick.toLowerCase().includes(val.toLowerCase()));
                                setSubtaskFilteredUsers(matches);
                                setSubtaskSelectedUserIndex(0);
                            }
                        }}
                        onKeyDown={handleSubtaskKeyDown}
                    />
                    {subtaskInputState === 0 && subtaskForm.formState.errors.title && (
                        <div
                            role="alert"
                            style={{ position: 'absolute', bottom: '-18px', left: '12px', fontSize: '10px', color: '#f87171' }}
                        >
                            {translate('modal.subtaskTitleRequired')}
                        </div>
                    )}
                    <div style={{ position: 'absolute', bottom: '0', left: '0', height: '2px', backgroundColor: '#10b981', transition: 'width 0.3s ease', width: `${(subtaskInputState / 4) * 100}%`, zIndex: 5, borderRadius: '0 0 4px 4px', pointerEvents: 'none' }} />
                </>
            )}
        </div>
    );
}
